/**
 * Known progress mask keys in svidashboarddemo-eu (Object{N}/{variant}/{quarter}Q{year} …).
 * Used when the backend progress-image lookup returns 404 but the object exists in S3.
 */
export type ProgressVariant = 'lightGreen' | 'darkGreen';

const progressImageKeys: Record<string, Record<ProgressVariant, string>> = {
  '4|2022|1': {
    lightGreen: 'Object4/lightGreen/1Q2022 18032022 - LIGHT green.png',
    darkGreen: '',
  },
  '4|2022|2': {
    lightGreen: 'Object4/lightGreen/2Q2022 15042022 - LIGHT green.png',
    darkGreen: 'Object4/darkGreen/2Q2022 15042022 - DARK green.png',
  },
  '4|2022|3': {
    lightGreen: 'Object4/lightGreen/3Q2022 04092022 - LIGHT green.png',
    darkGreen: 'Object4/darkGreen/3Q2022 04092022 - DARK green.png',
  },
  '4|2022|4': {
    lightGreen: 'Object4/lightGreen/4Q2022 15112022 - LIGHT green.png',
    darkGreen: 'Object4/darkGreen/4Q2022 15112022 - DARK green.png',
  },
  '4|2024|2': {
    lightGreen: 'Object4/lightGreen/2Q2024 31052024 - LIGHT green.png',
    darkGreen: '',
  },
  '4|2024|3': {
    lightGreen: '',
    darkGreen: 'Object4/darkGreen/3Q2024 19092024 - DARK green.png',
  },
  '4|2024|4': {
    lightGreen: '',
    darkGreen: 'Object4/darkGreen/4Q2024 21102024 - DARK green.png',
  },
  '5|2022|1': {
    lightGreen: 'Object5/lightGreen/1Q2022 19032022 - LIGHT green.png',
    darkGreen: '',
  },
  '5|2022|2': {
    lightGreen: 'Object5/lightGreen/2Q2022 14042022 - LIGHT green.png',
    darkGreen: 'Object5/darkGreen/2Q2022 14042022 - DARK green.png',
  },
  '5|2022|3': {
    lightGreen: 'Object5/lightGreen/3Q2022 12082022 - LIGHT green.png',
    darkGreen: 'Object5/darkGreen/3Q2022 12082022 - DARK green.png',
  },
  '5|2022|4': {
    lightGreen: '',
    darkGreen: 'Object5/darkGreen/4Q2022 12102022 - DARK green.png',
  },
  '5|2023|1': {
    lightGreen: 'Object5/lightGreen/1Q2023 28032023 - LIGHT green.png',
    darkGreen: '',
  },
  '5|2023|2': {
    lightGreen: '',
    darkGreen: 'Object5/darkGreen/2Q2023 29052023 - DARK green.png',
  },
  '5|2023|3': {
    lightGreen: '',
    darkGreen: 'Object5/darkGreen/3Q2023 16072023 - DARK green.png',
  },
  '5|2023|4': {
    lightGreen: '',
    darkGreen: 'Object5/darkGreen/4Q2023 14112023 - DARK green.png',
  },
  '5|2024|2': {
    lightGreen: 'Object5/lightGreen/2Q2024 19052024 - LIGHT green.png',
    darkGreen: '',
  },
  '5|2024|3': {
    lightGreen: '',
    darkGreen: 'Object5/darkGreen/3Q2024 22092024 - DARK green.png',
  },
  '5|2024|4': {
    lightGreen: '',
    darkGreen: 'Object5/darkGreen/4Q2024 17102024 - DARK green.png',
  },
};

function manifestLookupKey(
  objectNum: string,
  year: number,
  quarter: number,
): string {
  return `${objectNum}|${year}|${quarter}`;
}

function objectNumHasManifestEntries(objectNum: string): boolean {
  const prefix = `${objectNum}|`;
  return Object.keys(progressImageKeys).some((key) => key.startsWith(prefix));
}

export function getKnownProgressImageKey(
  objectNum: string,
  year: number,
  quarter: number,
  variant: ProgressVariant,
): string | null {
  const entry = progressImageKeys[manifestLookupKey(objectNum, year, quarter)];
  const key = entry?.[variant];
  return key && key.length > 0 ? key : null;
}

/**
 * Picks the S3 variant folder for a quarter. Objects 4/5 only have a subset of
 * light/dark masks uploaded; when the clicked variant is missing, use the other
 * if it exists. Objects without manifest entries keep the requested variant
 * (Object 0001 uses backend S3 listing the same way as before).
 */
export function resolveProgressVariantForQuarter(
  objectNum: string,
  year: number,
  quarter: number,
  requested: ProgressVariant,
): ProgressVariant | null {
  if (!objectNumHasManifestEntries(objectNum)) {
    return requested;
  }

  if (getKnownProgressImageKey(objectNum, year, quarter, requested)) {
    return requested;
  }

  const alternate: ProgressVariant =
    requested === 'lightGreen' ? 'darkGreen' : 'lightGreen';

  if (getKnownProgressImageKey(objectNum, year, quarter, alternate)) {
    return alternate;
  }

  return null;
}

export function parseProgressImageDateFromKey(key: string): string | null {
  const fileName = key.split('/').pop() ?? '';
  const match = fileName.match(/(\d{8})/);
  if (!match) {
    return null;
  }
  const dt = match[1];
  return `${dt.substring(0, 2)}-${dt.substring(2, 4)}-${dt.substring(4, 8)}`;
}
